import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeSpec } from './NodeSpec';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const activatePickLightNodeSpec: NodeSpec<NodeCfg<'activatePickLight'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'Pick Light',
    requiredSockets: [ 'channelNumber', 'pickLightId', 'activate' ],
    kindLabel: 'Activate Pick Light',
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.Ptl110PickLight ],
            },
            on: { 'update:value': defaultControlUpdateValueHandler },
            value: nodeCfg.controlValues.connectionId,
        }),
    }),
    cfg: () => ({
        id: getUID(),
        kind: 'activatePickLight',
        width: 220,
        controlValues: {
            connectionId: '',
        },
    }),
    inputs: nodeCfg => ({
        activate: {
            socket: 'event',
            label: 'Activate',
        },
        pickLightId: {
            socket: 'number',
            label: 'Light Id',
            controlOpts: defineControlOpts({
                value: nodeCfg.socketDefaults?.inputs?.pickLightId,
                cfg: {
                    min: 1,
                    max: 256,
                    kind: 'number',
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
        },
        channelNumber: {
            socket: 'number',
            label: 'Channel Number',
            controlOpts: defineControlOpts({
                value: nodeCfg.socketDefaults?.inputs?.channelNumber,
                cfg: {
                    min: 0,
                    max: 4,
                    kind: 'number',
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
        },
        color: {
            socket: 'string',
            label: 'Color',
            controlOpts: defineControlOpts({
                value: nodeCfg.socketDefaults?.inputs?.color,
                cfg: {
                    kind: 'selection',
                    items: [
                        {
                            value: 'red',
                            label: 'Red',
                        },
                        {
                            value: 'green',
                            label: 'Green',
                        },
                        {
                            value: 'yellow',
                            label: 'Yellow',
                        },
                        {
                            value: 'blue',
                            label: 'Blue',
                        },
                        {
                            value: 'magenta',
                            label: 'Magenta',
                        },
                        {
                            value: 'cyan',
                            label: 'Cyan',
                        },
                        {
                            value: 'white',
                            label: 'White',
                        },
                        {
                            value: 'amber',
                            label: 'Amber',
                        },
                        {
                            value: 'rose',
                            label: 'Rose',
                        },
                        {
                            value: 'limeGreen',
                            label: 'Lime Green',
                        },
                        {
                            value: 'orange',
                            label: 'Orange',
                        },
                        {
                            value: 'skyBlue',
                            label: 'Sky Blue',
                        },
                        {
                            value: 'violet',
                            label: 'Violet',
                        },
                        {
                            value: 'springGreen',
                            label: 'Spring Green',
                        },
                    ],
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
        },
        animation: {
            socket: 'string',
            label: 'Animation',
            controlOpts: defineControlOpts({
                value: nodeCfg.socketDefaults?.inputs?.animation,
                cfg: {
                    kind: 'selection',
                    items: [
                        {
                            value: 'steady',
                            label: 'Steady',
                        },
                        {
                            value: 'flash',
                            label: 'Flash',
                        },
                        {
                            value: 'twoColorFlash',
                            label: 'Two Color Flash',
                        },
                        {
                            value: 'halfHalfTopBottom',
                            label: 'Half/Half Top/Bottom',
                        },
                        {
                            value: 'halfHalfLeftRight',
                            label: 'Half/Half Left/Right',
                        },
                        {
                            value: 'halfHalfRotate',
                            label: 'Half/Half Rotate',
                        },
                        {
                            value: 'chase',
                            label: 'Chase',
                        },
                        {
                            value: 'intensitySweep',
                            label: 'Intensity Sweep',
                        },
                    ],
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
        },
        display: {
            socket: 'number',
            label: 'Display',
            controlOpts: defineControlOpts({
                value: nodeCfg.socketDefaults?.inputs?.display,
                cfg: {
                    min: 0,
                    max: 65535,
                    kind: 'number',
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
        },
    }),
    outputs: () => ({}),
    validator: node => node.controlValues.connectionId ? true : { connectionId: 'Connection id is required' },
};
